import React, { useEffect, useState } from "react";
import axios from "axios";
import { Loader } from "lucide-react";
import {
  FormControl,
  Select,
  MenuItem,
  OutlinedInput,
  Chip,
  Box,
} from "@mui/material";
import useUserRole from "../../app/auth/useUserRole";
import { getToken } from "../../app/auth/authConfig";

const UserResistrations = () => {
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [tools, setTools] = useState([]);
  const [email, setEmail] = useState("");
  const [role, setUserType] = useState("Member");
  const [isLoading, setIsLoading] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(true);
  const [toast, setToast] = useState(null);
  const currentUserRole = useUserRole();

  useEffect(() => {
    fetchTools();
  }, []);

  const fetchTools = async () => {
    try {
      setToolsLoading(true);
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/tools/list/`;
      const token = await getToken();
      const res = await axios.get(baseURL, {
        headers: { "oidc-token": token },
      });
      setTools(res.data);
    } catch (err) {
      console.error("Tool fetch error", err);
    } finally {
      setToolsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
  
    const payload = {
      email,
      role,
      toolAccess: selectedGroups,
    };
  
    try {
      const baseURL = `${process.env.REACT_APP_BACKEND_API_URL}/api/users/`;
      const token = await getToken();
  
      await axios.post(baseURL, payload, {
        headers: {
          "oidc-token": token,
        },
      });
  
      // Show success toast
      setToast({ type: "success", message: "✅ User registered successfully!" });
      setTimeout(() => setToast(null), 3000);

      setEmail("");
      setUserType("Member");
      setSelectedGroups([]);
    } catch (error) {
      console.error("User register error:", error);
      const errorMsg = error.response?.data?.message || "User registration failed";
      // Show error toast
      setToast({ type: "error", message: `❌ ${errorMsg}` });
      setTimeout(() => setToast(null), 3000);
    } finally {
      setIsLoading(false);
    }
  };
  

  const handleGroupChange = (event) => {
    const { value } = event.target;
    setSelectedGroups(typeof value === "string" ? value.split(",") : value);
  };

  const handleDelete = (event, groupToDelete) => {
    event.stopPropagation();
    setSelectedGroups((prev) => prev.filter((g) => g !== groupToDelete));
  };

  const availableGroups = tools
    .map((t) => t.toolName)
    .filter((name) => !selectedGroups.includes(name));

  return (
    <div
      style={{
        background: "#fff",
        borderRadius: 12,
        boxShadow: "0 2px 12px rgba(0,0,0,0.04)",
        padding: 32,
        margin: "0 auto",
      }}
    >
      {currentUserRole === null && (
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "flex-end" }}>
          <style>{`
            @keyframes shimmer {
              0% { background-position: -400px 0; }
              100% { background-position: 400px 0; }
            }
            .skeleton-box {
              background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
              background-size: 800px 100%;
              animation: shimmer 1.4s infinite;
              border-radius: 6px;
            }
          `}</style>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div className="skeleton-box" style={{ height: 14, width: "40%", marginBottom: 10 }} />
            <div className="skeleton-box" style={{ height: 48, width: "100%" }} />
          </div>
          <div style={{ flex: 1, minWidth: 160 }}>
            <div className="skeleton-box" style={{ height: 14, width: "30%", marginBottom: 10 }} />
            <div className="skeleton-box" style={{ height: 48, width: "100%" }} />
          </div>
          <div style={{ display: "flex", gap: 12 }}>
            <div className="skeleton-box" style={{ height: 40, width: 90 }} />
            <div className="skeleton-box" style={{ height: 40, width: 110 }} />
          </div>
        </div>
      )}
      {currentUserRole !== null && currentUserRole !== "Admin" && (
        <div
          style={{
            background: "#fee2e2",
            color: "#991b1b",
            padding: "16px",
            borderRadius: "8px",
            marginBottom: "24px",
            border: "1px solid #fca5a5",
          }}
        >
          <strong>Access Denied:</strong> Only admins can register new users.
        </div>
      )}
      <form
        onSubmit={handleSubmit}
        style={{
          display: currentUserRole === "Admin" ? "flex" : "none",
          gap: 24,
          alignItems: "center",
          marginBottom: 24,
          flexWrap: "wrap" 
        }}
      >
        {/* Email */}
        <div style={{ flex: 1 }}>
          <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            Email *
          </label>
          <input
            type="email"
            placeholder="Email Address"
            required
            disabled={toolsLoading}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 16,
              height: 48,
              backgroundColor: toolsLoading ? "#f0f0f0" : "white",
              cursor: toolsLoading ? "not-allowed" : "auto",
            }}
          />
        </div>

        {/* User Type */}
        <div style={{ flex: 1 }}>
          <label style={{ fontWeight: 500, marginBottom: 8, display: "block" }}>
            User Type *
          </label>
          <select
            value={role}
            disabled={toolsLoading}
            onChange={(e) => setUserType(e.target.value)}
            style={{
              width: "100%",
              padding: 10,
              borderRadius: 4,
              border: "1px solid #ccc",
              fontSize: 16,
              height: 48,
              backgroundColor: toolsLoading ? "#f0f0f0" : "white",
              cursor: toolsLoading ? "not-allowed" : "auto",
            }}
          >
            <option value="Member">Member</option>
            <option value="Admin">Admin</option>
          </select>
        </div>

        {/* Buttons — moved inside form, CSS same */}
        <div style={{ display: "flex", gap: 16, width: "100%" }}>
        <button
            type="reset"
            disabled={toolsLoading}
            onClick={() => setSelectedGroups([])}
            style={{
              background: toolsLoading ? "#f5f5f5" : "#ffffff",
              color: toolsLoading ? "#ccc" : "#bb1ccc",
              border: toolsLoading ? "1px solid #e0e0e0" : "1px solid #bb1ccc",
              borderRadius: 4,
              padding: "8px 32px",
              fontWeight: 600,
              fontSize: 16,
              cursor: toolsLoading ? "not-allowed" : "pointer",
              transition: "all 0.3s ease",
            }}
          >
            Reset
          </button>

          <button
            type="submit"
            disabled={isLoading || toolsLoading}
            style={{
              background: (isLoading || toolsLoading) ? "#d4a5e6" : "#bb1ccc",
              color: "#fff",
              border: "none",
              borderRadius: 4,
              padding: "8px 32px",
              fontWeight: 600,
              fontSize: 16,
              cursor: (isLoading || toolsLoading) ? "not-allowed" : "pointer",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              transition: "all 0.3s ease",
            }}
          >
            {isLoading && <Loader size={16} className="animate-spin" />}
            {isLoading ? "Registering..." : "Register"}
          </button>
        </div>
      </form>

      {/* Toast Notification */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: "20px",
            right: "20px",
            padding: "16px 24px",
            borderRadius: "8px",
            background: toast.type === "success" ? "#10b981" : "#ef4444",
            color: "white",
            fontSize: "14px",
            fontWeight: "600",
            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
            animation: "slideIn 0.3s ease",
            zIndex: 9999,
          }}
        >
          {toast.message}
        </div>
      )}

      <style>{`
        @keyframes slideIn {
          from {
            transform: translateX(400px);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .animate-spin {
          animation: spin 1s linear infinite;
        }
      `}</style>
    </div>
  );
};

export default UserResistrations;
